import { useEffect } from "react"
import { useAuthContext } from "@context/AuthProvider"
import { AuthService } from "@services/authService"
import styles from "./WebChatWidget.module.css"
import { eventTrackingItems } from "@utils/eventTracking"
import { getRuntimeEnv } from "@utils/env"

// Declare the global WebChatWidgetProps interface
declare global {
	interface Window {
		WebChatWidgetProps: {
			z: string
			appId: string
			domain: string
			environment: string
			providerCode: string
			agentEmail: string
			agentFirstName: string
			agentLastName: string
			betaVersion: boolean
			sessionCreatedTime: number
			baseUrl: string
			token: string
			idToken: string
		}
		$: JQueryStatic
		jQuery: JQueryStatic
		wcwConfig?: {
			domain: string
			env: string
			email: string
			first_name: string
			last_name: string
			beta: boolean
			passthrough: string
		}
		WebChatWidgetGlobals?: {
			launch: HTMLElement
			icon: HTMLElement
			iconAlert: HTMLElement
			iconText: HTMLElement
			chatContainer: HTMLElement
			listenersInitialized: boolean
			redOneHideFunction: () => void
			slideIn: () => void
			displayAnimationText: () => void
			slideOut: () => void
			[key: string]: unknown
		}
		Pypestream: {
			config: (config: unknown) => void
			boot: (options: { APP_ID: string }, element: HTMLElement) => void
			toggle: () => void
			shutdown: () => void
			onChatRestart: (callback: () => void) => void
			onShow: (callback: () => void) => void
			onHide: (callback: () => void) => void
		}
	}
}

export const WebChatWidget = () => {
	const { isAuthenticated } = useAuthContext()
	const { userProfile } = useAuthContext()

	useEffect(() => {
		if (isAuthenticated && userProfile?.latteAvailable) {
			// Create launch button elements
			const launchButton = document.createElement("div")
			launchButton.id = "launch"
			launchButton.className = styles.launchButton

			const icon = document.createElement("div")
			icon.id = "icon"
			icon.className = styles.chatIcon

			const alert = document.createElement("div")
			alert.id = "launchIcon-alert"
			alert.className = `${styles["chatIcon-alert"]} ${styles.hide}`
			alert.innerHTML = "<span>1</span>"

			const text = document.createElement("div")
			text.id = "icon-text"
			text.className = styles["chatIcon-text"]
			text.innerHTML = "<span>Chat with Latte</span>"

			launchButton.appendChild(text)
			launchButton.appendChild(icon)
			launchButton.appendChild(alert)
			document.body.appendChild(launchButton)

			// Create a hidden logout button for the widget
			const logoutBtn = document.createElement("button")
			logoutBtn.id = "logoutBtn"
			logoutBtn.style.display = "none"
			document.body.appendChild(logoutBtn)

			launchButton?.addEventListener("click", () => {
				eventTrackingItems({
					event_name: "click",
					link_url: "",
					category: "Web Chat",
					interactionType: "Chat Start",
					navLinkText: "Chat with Latte"
				})
			})

			// Set up WebChatWidgetProps
			const initializeProps = async () => {
				const { remoteAccessToken } = await AuthService.webserviceAccess(
					getRuntimeEnv("VITE_WEBCHAT_PROVIDER_CODE")
				)
				window.WebChatWidgetProps = {
					z: remoteAccessToken,
					appId: userProfile.latteKey || "",
					domain: getRuntimeEnv("VITE_WEBCHAT_DOMAIN"),
					environment: getRuntimeEnv("VITE_WEBCHAT_ENVIRONMENT"),
					providerCode: getRuntimeEnv("VITE_WEBCHAT_PROVIDER_CODE"),
					agentEmail: userProfile.agentEmailAddress || "",
					agentFirstName: userProfile.agentFirstName || "",
					agentLastName: userProfile.agentLastName || "",
					betaVersion: getRuntimeEnv("VITE_WEBCHAT_BETA_VERSION") === "true",
					sessionCreatedTime: Number(AuthService.getLoginDate()) || Date.now(),
					baseUrl: getRuntimeEnv("VITE_BASE_URL"),
					token: AuthService.getAuthToken() || "",
					idToken: AuthService.getIdToken() || ""
				}
			}

			const loadScript = (src: string): Promise<void> => {
				return new Promise((resolve, reject) => {
					const script = document.createElement("script")
					script.src = src
					script.async = true
					script.onload = () => resolve()
					script.onerror = () =>
						reject(new Error(`Failed to load script: ${src}`))
					document.body.appendChild(script)
				})
			}

			const initializeWebChat = async () => {
				try {
					await initializeProps()

					// Load jQuery first
					await loadScript("https://code.jquery.com/jquery-3.6.0.min.js")

					// Then load Pypestream
					await loadScript(getRuntimeEnv("VITE_WEBCHAT_PUBLIC_URL"))

					// Finally load our widget script
					const uiBaseUrl = getRuntimeEnv("VITE_UI_BASE_URL")
					await loadScript(
						`${uiBaseUrl}${uiBaseUrl !== "/" ? "/" : ""}webChatWidget.js`
					)
				} catch (error) {
					console.error("Failed to initialize WebChat:", error)
				}
			}

			initializeWebChat()

			return () => {
				// Cleanup
				const launchButton = document.getElementById("launch")
				const logoutBtn = document.getElementById("logoutBtn")
				if (launchButton) document.body.removeChild(launchButton)
				if (logoutBtn) document.body.removeChild(logoutBtn)

				// Shutdown Pypestream if it exists
				if (window.Pypestream) {
					// Call shutdown first
					const pypestream = window.Pypestream
					pypestream.shutdown()

					// Clean up the Pypestream instance from the window object
					// We need to use 'any' here because we're intentionally setting a required property to undefined
					// biome-ignore lint/suspicious/noExplicitAny: Intentionally using any to set a required property to undefined
					;(window as any).Pypestream = undefined
				}

				// Clean up the wcwConfig from the window object
				if (window.wcwConfig) {
					// We need to use 'any' here because we're intentionally setting a required property to undefined
					// biome-ignore lint/suspicious/noExplicitAny: Intentionally using any to set a required property to undefined
					;(window as any).wcwConfig = undefined
				}

				// Clean up the WebChatWidgetGlobals from the window object
				if (window.WebChatWidgetGlobals) {
					// We need to use 'any' here because we're intentionally setting a required property to undefined
					// biome-ignore lint/suspicious/noExplicitAny: Intentionally using any to set a required property to undefined
					;(window as any).WebChatWidgetGlobals = undefined
				}

				// Remove scripts
				const scripts = document.querySelectorAll("script")
				for (const script of scripts) {
					if (
						script.src.includes("webChatWidget.js") ||
						script.src.includes("jquery") ||
						script.src.includes(getRuntimeEnv("VITE_WEBCHAT_PUBLIC_URL"))
					) {
						document.body.removeChild(script)
					}
				}

				// Remove widget container
				const wcwContainer = document.getElementById("wcwContainer")
				if (wcwContainer) {
					document.body.removeChild(wcwContainer)
				}
			}
		}
	}, [isAuthenticated, userProfile?.latteAvailable])

	return null
}
